import { useState, useEffect } from 'react';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import axios from 'axios';

import Message from '../../components/Message';
import Loader from '../../components/Loader';
import Paginate from '../../components/Paginate';

import {
  useGetTenantsQuery,
  useDeleteTenantMutation,
  useCreateTenantMutation,
} from '../../slices/tenantsApiSlice';

const TenantListScreen = () => {
  const { pageNumber } = useParams();
  const today = new Date().toISOString().split('T')[0];

  const [filter, setFilter] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [latestEnds, setLatestEnds] = useState({});
  const [loadingEnds, setLoadingEnds] = useState(false);

  const {
    data: tenantData,
    isLoading: loadingTenants,
    error: tenantsError,
    refetch: refetchTenants,
  } = useGetTenantsQuery({ pageNumber });

  const [deleteTenant, { isLoading: loadingDelete }] = useDeleteTenantMutation();
  const [createTenant, { isLoading: loadingCreate }] = useCreateTenantMutation();

  const tenants = tenantData?.tenants || [];

  const fetchLatestEndDates = async () => {
    setLoadingEnds(true);
    const results = {};
    await Promise.all(
      tenants.map(async (t) => {
        try {
          const { data } = await axios.get(`/api/rents/tenant/${t._id}/latest`);
          results[t._id] = data?.endDate ? new Date(data.endDate) : null;
        } catch {
          results[t._id] = null;
        }
      })
    );
    setLatestEnds(results);
    setLoadingEnds(false);
  };

  useEffect(() => {
    if (tenants.length > 0) {
      fetchLatestEndDates();
    }
  }, [tenants]);

  const calculateDaysLeft = (endDate) => {
    if (!endDate) return null;
    const diff = new Date(endDate) - new Date(today);
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const deleteHandler = async (id) => {
    if (!window.confirm('Delete this tenant?')) return;
    try {
      await deleteTenant(id).unwrap();
      toast.success('Tenant deleted');
      refetchTenants();
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const createHandler = async () => {
    if (!window.confirm('Create default new tenant?')) return;
    try {
      await createTenant({
        name: 'Sample Tenant',
        house: '67e2bb1d3f5e2c8008364184',
        address: 'Default Address',
        type: 'Apartment',
        monthlyRent: 0,
        securityDeposit: 0,
        description: 'Default Tenant',
        landlord: '691a7c792e4443d49830e91d',
        startDate: today,
      }).unwrap();
      toast.success('Tenant created');
      refetchTenants();
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const filteredTenants = tenants.filter((t) => {
    const term = filter.toLowerCase();
    const matchesText =
      t.name?.toLowerCase().includes(term) ||
      t.address?.toLowerCase().includes(term) ||
      t.type?.toLowerCase().includes(term);

    const endDate = latestEnds[t._id];
    const isExpired = endDate && new Date(endDate) < new Date(today);

    return matchesText && (!showExpiredOnly || isExpired);
  });

  const groupedTenants = groupBy
    ? filteredTenants.reduce((acc, tenant) => {
        const key = groupBy === 'house' ? tenant.house?._id : tenant.landlord?._id;
        if (!acc[key]) {
          acc[key] = {
            group: groupBy === 'house' ? tenant.house : tenant.landlord,
            tenants: [],
          };
        }
        acc[key].tenants.push(tenant);
        return acc;
      }, {})
    : null;

  const exportToExcel = () => {
    const data = filteredTenants.map((t) => {
      const endDate = latestEnds[t._id];
      const daysLeft = calculateDaysLeft(endDate);
      return {
        Name: t.name,
        House: t.house?.name,
        Address: t.address,
        Type: t.type,
        MonthlyRent: t.monthlyRent,
        StartDate: t.startDate,
        EndDate: endDate ? endDate.toISOString().split('T')[0] : 'No Data',
        Today: today,
        DaysLeft: daysLeft !== null ? daysLeft : 'No Data',
        Landlord: t.landlord?.name,
        Contact: t.contact,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tenants');
    XLSX.writeFile(workbook, 'TenantList.xlsx');
  };

  const renderTenantRow = (t) => {
    const endDate = latestEnds[t._id];
    const daysLeft = calculateDaysLeft(endDate);
    const isExpired = daysLeft !== null && daysLeft < 0;

    return (
      <tr key={t._id} style={{ backgroundColor: isExpired ? '#f8d7da' : undefined }}>
        <td>{t.name}</td>
        <td>{t.address}</td>
        <td>{t.type}</td>
        <td>{t.monthlyRent}</td>
        <td>{t.startDate?.substring(0, 10)}</td>
        <td>{endDate ? new Date(endDate).toLocaleDateString() : 'No Data'}</td>
        <td>{today}</td>
        <td>{daysLeft !== null ? daysLeft : 'No Data'}</td>
        <td>{t.landlord?.name}</td>
        <td>{t.contact}</td>
        <td>
          <Button as={Link} to={`/tenant/${t._id}`} variant="light" className="btn-sm">Details</Button>{' '}
          <Button as={Link} to={`/admin/tenant/${t._id}/edit`} variant="light" className="btn-sm me-2"><FaEdit /></Button>{' '}
          <Button variant="danger" className="btn-sm" onClick={() => deleteHandler(t._id)}>
            <FaTrash style={{ color: 'white' }} />
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col><h1>Tenants</h1></Col>
        <Col className="text-end">
          <Button onClick={createHandler}><FaPlus /> Create Tenant</Button>{' '}
          <Button variant="success" onClick={exportToExcel}>📥 Export</Button>{' '}
          <Link to='/admin/tenantimportscreen' className='btn btn-light my-3'>AddViaExcel</Link>{' '}
          <Link to='/admin/tenanteditimportscreen' className='btn btn-light my-3'>EditViaExcel</Link>{' '}
          <Link to='/create/tenant' className='btn btn-light my-3'>Add Tenant</Link>
          
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by name, address, or type"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="">No Grouping</option>
            <option value="house">Group by House</option>
            <option value="landlord">Group by Landlord</option>
          </Form.Select>
        </Col>
        <Col md={3} className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            label="Show only expired"
            checked={showExpiredOnly}
            onChange={(e) => setShowExpiredOnly(e.target.checked)}
          />
        </Col>
      </Row>

      {(loadingTenants || loadingCreate || loadingDelete || loadingEnds) ? (
        <Loader />
      ) : tenantsError ? (
        <Message variant="danger">{tenantsError?.data?.message}</Message>
      ) : groupedTenants ? (
        Object.values(groupedTenants).map(({ group, tenants }) => (
          

            <div key={group?._id || 'unknown'} className="mb-4">
            <h5 className="text-primary">Group: {group?.name || 'Unknown'}</h5>
            <Table striped bordered hover responsive className="table-sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Type</th>
                  <th>Rent</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Today</th>
                  <th>Days Left</th>
                  <th>Landlord</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(renderTenantRow)}
              </tbody>
            </Table>
          </div>
        ))
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Type</th>
                <th>Rent</th>
                <th>Start</th>
                <th>End</th>
                <th>Today</th>
                <th>Days Left</th>
                <th>Landlord</th>
                <th>Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.map(renderTenantRow)}
            </tbody>
          </Table>

          <Paginate
            pages={tenantData?.pages || 1}
            page={tenantData?.page || 1}
            isAdmin
            type="tenantlist"
          />
        </>
      )}
    </>
  );
};

export default TenantListScreen;